import { useModel as _useModel, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, isRef as _isRef, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex flex-wrap items-start gap-4" }
const _hoisted_2 = { class: "w-72" }
const _hoisted_3 = { class: "w-48" }
const _hoisted_4 = {
  key: 1,
  class: "py-10 text-center text-gray-500 space-y-4"
}
const _hoisted_5 = {
  key: 2,
  class: "py-10 flex justify-center"
}
const _hoisted_6 = {
  key: 3,
  class: "py-10 text-center text-gray-500"
}
const _hoisted_7 = {
  key: 4,
  class: "max-h-[50vh] overflow-auto rounded-lg border border-gray-200"
}
const _hoisted_8 = { class: "w-full text-normal" }
const _hoisted_9 = { class: "sticky top-0 z-10 bg-gray-50 text-left text-gray-500" }
const _hoisted_10 = { class: "px-3 py-2 font-medium" }
const _hoisted_11 = { class: "w-28 px-3 py-2 font-medium" }
const _hoisted_12 = { class: "w-40 px-3 py-2 font-medium" }
const _hoisted_13 = { class: "px-3 py-2 text-right font-medium" }
const _hoisted_14 = { class: "w-36 px-3 py-2 font-medium" }
const _hoisted_15 = { class: "px-3 py-2 font-medium" }
const _hoisted_16 = { class: "px-3 py-3" }
const _hoisted_17 = { class: "px-3 py-2" }
const _hoisted_18 = { class: "text-gray-500" }
const _hoisted_19 = { class: "text-sm text-gray-500" }
const _hoisted_20 = { class: "px-3 py-2" }
const _hoisted_21 = {
  key: 0,
  class: "pt-1 text-sm text-red-600"
}
const _hoisted_22 = { class: "px-3 py-2" }
const _hoisted_23 = { class: "flex items-center gap-1.5" }
const _hoisted_24 = { class: "text-sm text-gray-500" }
const _hoisted_25 = {
  key: 0,
  class: "pt-1 text-sm text-red-600"
}
const _hoisted_26 = { class: "px-3 py-2 text-right whitespace-nowrap" }
const _hoisted_27 = {
  key: 0,
  class: "font-semibold text-gray-900"
}
const _hoisted_28 = {
  key: 1,
  class: "text-gray-400",
  "aria-hidden": "true"
}
const _hoisted_29 = { class: "px-3 py-2" }
const _hoisted_30 = { class: "px-3 py-2" }
const _hoisted_31 = {
  key: 0,
  class: "pt-1 text-sm text-red-600"
}
const _hoisted_32 = {
  key: 5,
  class: "text-sm text-red-600",
  role: "alert"
}

import { computed } from 'vue';
import { parseISO } from 'date-fns';
import { useToast } from 'vue-toastification';
import { Loader2 } from 'lucide-vue-next';
import CustomSelect from '@/components/ui/CustomSelect.vue';
import DatePicker from '@/components/ui/DatePicker.vue';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogClose, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import Spinner from '@/components/ui/Spinner.vue';
import { bulkPurchasePayload, entryFor, evaluateBulkPurchaseRow, isBulkPurchaseEntryDirty, type BulkPurchaseRowProblem } from '@/composables/inventory/bulkPurchase';
import { useBulkEntryDialog } from '@/composables/inventory/useBulkEntryDialog';
import { drugUnitValue, formatQuantity } from '@/composables/inventory/inventoryTable';
import { i18n } from '@/plugins/internationalization/i18n';
import { useMedicineBulkPurchase, useSaveMedicineBulkPurchase } from '@/plugins/store/actions/queries/inventory';
import type { MedicineBulkPurchaseRow } from '@/types';
import UnsavedInputWarning from './UnsavedInputWarning.vue';

/*
 * The farm-wide purchase, the counterpart of the bulk count: every drug the farm has an instruction for in one
 * table, the bought ones ticked, each starting from the packs and pack size it was last bought in.
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'BulkPurchaseDialog',
  props: {
    "open": { type: Boolean, ...{ required: true } },
    "openModifiers": {},
  },
  emits: ["update:open"],
  setup(__props) {

const isOpen = _useModel<boolean>(__props, 'open');

const toast = useToast();
const t = (key: string, params: Record<string, unknown> = {}) => i18n.global.t(`views.health.inventory.bulkPurchase.${key}`, params);

// Every opening starts from a blank purchase. What was typed for a row survives a change of date, not of farm; a row
// left untouched starts again from the last purchase on or before the new date.
const {
    writableFarms,
    farmId,
    dateValue,
    dateOnly,
    todayIso,
    entries,
    serverErrors,
    generalErrors,
    rows,
    isFetching,
    isError,
    refetch,
    isSaving,
    submit,
    pendingAction,
    currentFarmName,
    requestFarm,
    requestOpen,
    confirm,
    keep,
} = useBulkEntryDialog({ isOpen, useRows: useMedicineBulkPurchase, useSave: useSaveMedicineBulkPurchase, entryFor, isEntryDirty: isBulkPurchaseEntryDirty });

const states = computed(() =>
    rows.value.flatMap((row) => {
        const entry = entries[drugUnitValue(row)];

        return entry ? [{ row, entry, state: evaluateBulkPurchaseRow(entry) }] : [];
    })
);
const boughtRows = computed(() => states.value.filter(({ state }) => !state.skipped));
const hasProblems = computed(() => boughtRows.value.some(({ state }) => state.problems.length > 0));
const canSave = computed(() => farmId.value !== null && dateOnly.value !== '' && boughtRows.value.length > 0 && !hasProblems.value && !isFetching.value && !isSaving.value);

function problemText(problem: BulkPurchaseRowProblem): string {
    return t(`problems.${problem}`);
}

/** Under the drug: when it was last bought, which is what its packs and pack size start from. */
function lastPurchaseText(row: MedicineBulkPurchaseRow): string {
    return row.last_purchase ? t('lastPurchase', { date: i18n.global.d(parseISO(row.last_purchase.date), 'short') }) : t('firstPurchase');
}

async function save() {
    if (!canSave.value || farmId.value === null) {
        return;
    }

    const saved = await submit(bulkPurchasePayload(farmId.value, dateOnly.value, rows.value, entries));

    if (saved === null) {
        toast.error(t('saveFailed'));
    } else {
        toast.success(t('saved', { count: saved }));
    }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Dialog), {
    "model-value": isOpen.value,
    disabled: _unref(isSaving),
    class: "sm:max-w-[1100px]",
    "onUpdate:modelValue": _unref(requestOpen)
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(DialogHeader), null, {
        default: _withCtx(() => [
          _createVNode(_unref(DialogTitle), null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(t('title')), 1)
            ]),
            _: 1
          }),
          _createVNode(_unref(DialogDescription), { class: "mt-2 text-gray-500" }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(t('description')), 1)
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(CustomSelect, {
            value: _unref(farmId),
            label: _ctx.$t('views.health.inventory.table.headings.farm'),
            items: _unref(writableFarms),
            "item-text": "name",
            "item-value": "farm_id",
            required: "",
            "allow-empty": false,
            "onUpdate:value": _unref(requestFarm)
          }, null, 8, ["value", "label", "items", "onUpdate:value"])
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(DatePicker, {
            value: _unref(dateValue),
            "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => (_isRef(dateValue) ? (dateValue).value = $event : null)),
            resetable: false,
            required: "",
            max: _unref(todayIso),
            label: _ctx.$t('views.health.inventory.table.headings.date')
          }, null, 8, ["value", "max", "label"])
        ])
      ]),
      (_unref(pendingAction)?.kind === 'switchFarm')
        ? (_openBlock(), _createBlock(UnsavedInputWarning, {
            key: 0,
            action: "switchFarm",
            "farm-name": _unref(currentFarmName),
            onConfirm: _unref(confirm),
            onKeep: _unref(keep)
          }, null, 8, ["farm-name", "onConfirm", "onKeep"]))
        : _createCommentVNode("", true),
      (_unref(isError))
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", null, _toDisplayString(_ctx.$t('views.pigs.records.errors.couldNotLoadData')), 1),
            _createVNode(_unref(Button), {
              variant: "outline",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(refetch)()))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('views.pigs.records.errors.retry')), 1)
              ]),
              _: 1
            })
          ]))
        : (_unref(isFetching) && _unref(rows).length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(Spinner, { class: "size-6 animate-spin" })
            ]))
          : (_unref(rows).length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(t('empty')), 1))
            : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createElementVNode("table", _hoisted_8, [
                  _createElementVNode("thead", _hoisted_9, [
                    _createElementVNode("tr", null, [
                      _cache[2] || (_cache[2] = _createElementVNode("th", { class: "w-10 px-3 py-2" }, null, -1)),
                      _createElementVNode("th", _hoisted_10, _toDisplayString(_ctx.$t('views.health.inventory.table.headings.drug')), 1),
                      _createElementVNode("th", _hoisted_11, _toDisplayString(t('headings.packs')), 1),
                      _createElementVNode("th", _hoisted_12, _toDisplayString(t('headings.packSize')), 1),
                      _createElementVNode("th", _hoisted_13, _toDisplayString(t('headings.total')), 1),
                      _createElementVNode("th", _hoisted_14, _toDisplayString(t('headings.batchNumber')), 1),
                      _createElementVNode("th", _hoisted_15, _toDisplayString(t('headings.note')), 1)
                    ])
                  ]),
                  _createElementVNode("tbody", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(states.value, ({ row, entry, state }) => {
                      return (_openBlock(), _createElementBlock("tr", {
                        key: _unref(drugUnitValue)(row),
                        class: _normalizeClass(["border-t border-gray-200 align-top", { 'bg-gray-50 text-gray-400': state.skipped }])
                      }, [
                        _createElementVNode("td", _hoisted_16, [
                          _createVNode(_unref(Checkbox), {
                            checked: entry.bought,
                            "aria-label": t('include', { drug: row.drug_name }),
                            "onUpdate:checked": ($event: any) => (entry.bought = $event === true)
                          }, null, 8, ["checked", "aria-label", "onUpdate:checked"])
                        ]),
                        _createElementVNode("td", _hoisted_17, [
                          _createElementVNode("div", null, [
                            _createElementVNode("span", {
                              class: _normalizeClass(["font-medium", { 'text-gray-900': !state.skipped }])
                            }, _toDisplayString(row.drug_name), 3),
                            _createElementVNode("span", _hoisted_18, " (" + _toDisplayString(row.unit_name) + ")", 1)
                          ]),
                          _createElementVNode("div", _hoisted_19, _toDisplayString(lastPurchaseText(row)), 1)
                        ]),
                        _createElementVNode("td", _hoisted_20, [
                          _createVNode(_unref(Input), {
                            modelValue: entry.packs,
                            "onUpdate:modelValue": ($event: any) => ((entry.packs) = $event),
                            type: "number",
                            inputmode: "numeric",
                            min: "1",
                            step: "1",
                            autocomplete: "off",
                            class: _normalizeClass(["h-9 bg-white", { 'border-red-500': state.problems.includes('invalidPacks') }]),
                            "aria-label": `${t('headings.packs')} ${row.drug_name}`,
                            onInput: ($event: any) => (entry.bought = true)
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "class", "aria-label", "onInput"]),
                          (state.problems.includes('invalidPacks'))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_21, _toDisplayString(problemText('invalidPacks')), 1))
                            : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("td", _hoisted_22, [
                          _createElementVNode("div", _hoisted_23, [
                            _createVNode(_unref(Input), {
                              modelValue: entry.packSize,
                              "onUpdate:modelValue": ($event: any) => ((entry.packSize) = $event),
                              type: "number",
                              inputmode: "decimal",
                              min: "0",
                              step: "any",
                              autocomplete: "off",
                              class: _normalizeClass(["h-9 bg-white", { 'border-red-500': state.problems.includes('invalidPackSize') }]),
                              "aria-label": `${t('headings.packSize')} ${row.drug_name}`,
                              onInput: ($event: any) => (entry.bought = true)
                            }, null, 8, ["modelValue", "onUpdate:modelValue", "class", "aria-label", "onInput"]),
                            _createElementVNode("span", _hoisted_24, _toDisplayString(row.unit_name), 1)
                          ]),
                          (state.problems.includes('invalidPackSize'))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_25, _toDisplayString(problemText('invalidPackSize')), 1))
                            : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("td", _hoisted_26, [
                          (state.total !== null)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_27, _toDisplayString(_unref(formatQuantity)(state.total)) + " " + _toDisplayString(row.unit_name), 1))
                            : (_openBlock(), _createElementBlock("span", _hoisted_28, "-"))
                        ]),
                        _createElementVNode("td", _hoisted_29, [
                          _createVNode(_unref(Input), {
                            modelValue: entry.batchNumber,
                            "onUpdate:modelValue": ($event: any) => ((entry.batchNumber) = $event),
                            type: "text",
                            maxlength: "100",
                            autocomplete: "off",
                            class: "h-9 bg-white",
                            "aria-label": `${t('headings.batchNumber')} ${row.drug_name}`,
                            onInput: ($event: any) => (entry.bought = true)
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "aria-label", "onInput"])
                        ]),
                        _createElementVNode("td", _hoisted_30, [
                          _createVNode(_unref(Input), {
                            modelValue: entry.note,
                            "onUpdate:modelValue": ($event: any) => ((entry.note) = $event),
                            type: "text",
                            maxlength: "500",
                            autocomplete: "off",
                            class: "h-9 bg-white",
                            "aria-label": `${t('headings.note')} ${row.drug_name}`,
                            onInput: ($event: any) => (entry.bought = true)
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "aria-label", "onInput"]),
                          (_unref(serverErrors)[_unref(drugUnitValue)(row)])
                            ? (_openBlock(), _createElementBlock("div", _hoisted_31, _toDisplayString(_unref(serverErrors)[_unref(drugUnitValue)(row)]), 1))
                            : _createCommentVNode("", true)
                        ])
                      ], 2))
                    }), 128))
                  ])
                ])
              ])),
      (_unref(generalErrors).length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_32, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(generalErrors), (message) => {
              return (_openBlock(), _createElementBlock("div", { key: message }, _toDisplayString(message), 1))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      (_unref(pendingAction)?.kind === 'close')
        ? (_openBlock(), _createBlock(UnsavedInputWarning, {
            key: 6,
            action: "close",
            "farm-name": _unref(currentFarmName),
            onConfirm: _unref(confirm),
            onKeep: _unref(keep)
          }, null, 8, ["farm-name", "onConfirm", "onKeep"]))
        : _createCommentVNode("", true),
      _createVNode(_unref(DialogFooter), { class: "pt-2" }, {
        default: _withCtx(() => [
          _createVNode(_unref(DialogClose), { "as-child": "" }, {
            default: _withCtx(() => [
              _createVNode(_unref(Button), {
                variant: "outline",
                class: "min-w-32",
                disabled: _unref(isSaving)
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(t('cancel')), 1)
                ]),
                _: 1
              }, 8, ["disabled"])
            ]),
            _: 1
          }),
          _createVNode(_unref(Button), {
            class: "min-w-44",
            disabled: !canSave.value && !_unref(isSaving),
            "aria-busy": _unref(isSaving),
            onClick: save
          }, {
            default: _withCtx(() => [
              (_unref(isSaving))
                ? (_openBlock(), _createBlock(_unref(Loader2), {
                    key: 0,
                    class: "size-4 animate-spin",
                    "aria-hidden": "true"
                  }))
                : _createCommentVNode("", true),
              _createTextVNode(" " + _toDisplayString(t('save', { count: boughtRows.value.length })), 1)
            ]),
            _: 1
          }, 8, ["disabled", "aria-busy"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["model-value", "disabled", "onUpdate:modelValue"]))
}
}

})